import React, { useContext, useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';

import Slider from '../../../../components/slider';
import { Collection } from "../../../../types/collections";
import Button from "../../../../components/button";

import styles from './dialogs.module.scss';
import { useDispatch } from "react-redux";
import { invalidateCollectionUpdateState, toggleCollectionInternal, toggleCollectionTemplate } from "../../../../redux/actions/collections";
import { useSelector } from "react-redux";
import Wizard from "src/components/wizard";
import { ASSET_VIEWERS, DEFAULT_ASSET_VIEWER, DEFAULT_EXPERIENCE, EXPERIENCES } from "src/constants/collections";
import subscriptionPlanService from "src/services/subscriptionPlan.service";
import { AssetViewerSelector, ExperienceSelector } from "src/pages/collections/components/create";
import GenerateLink from "src/pages/asset/components/generateLink";
import { CollectionContext } from "../context";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";

interface TemplateConfirmationDialogProps {
  show: boolean;
  collection: Collection;
  onClose: () => void;
}

export const TemplateConfirmationDialog: React.FC<TemplateConfirmationDialogProps> = ({ show, collection, onClose }) => {
  const dispatch = useDispatch();

  const { trackTemplateToggled } = useCollectionsAdoption();

  const template = useMemo(() => collection.collectionTemplate, [show, collection]);
  const updating = useSelector((state: any) => _.get(state, `collections.update['${collection.id}'].updating`, false));
  const updated = useSelector((state: any) => _.get(state, `collections.update['${collection.id}'].updated`, false));

  const handleConfirm = () => {
    dispatch(toggleCollectionTemplate({
      id: collection.id as string,
      value: !template
    }));
  }

  useEffect(() => {
    if (show && updated) {
      trackTemplateToggled({
        collection: collection,
        template: template || false
      });

      dispatch(invalidateCollectionUpdateState());
      onClose();
    }
  }, [show, updated]);

  return (
    <Slider.Dialog show={show} width='300' className={styles.confrimationDialog} onClose={onClose}>
      <Slider.Header title={
        template ? 'turn OFF template' : 'turn ON template'
      } />
      <Slider.Body>
        <p className={styles.message}>
          {template ?
            'Turning this feature OFF will remove this Collection from templates.' :
            'Flip this switch to mark this collection as a template. All templates can be found in the `Template` folder — easy peasy!'
          }
        </p>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary"
          icon="paperflite-tick"
          text={updating ? 'Updating...' : 'Confirm'}
          disabled={updating}
          onClick={handleConfirm}
        />
        <Button text="Cancel" disabled={updating} onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface InternalConfirmationDialogProps {
  show: boolean;
  collection: Collection;
  onClose: () => void;
}

export const InternalConfirmationDialog: React.FC<InternalConfirmationDialogProps> = ({ show, collection, onClose }) => {
  const dispatch = useDispatch();
  const { trackInternalUserToggled } = useCollectionsAdoption();

  const internal = useMemo(() => collection.internal, [show, collection]);
  const updating = useSelector((state: any) => _.get(state, `collections.update['${collection.id}'].updating`, false));
  const updated = useSelector((state: any) => _.get(state, `collections.update['${collection.id}'].updated`, false));

  const handleConfirm = () => {
    dispatch(toggleCollectionInternal({
      id: collection.id as string,
      value: !internal
    }));
  }

  useEffect(() => {
    if (show && updated) {
      trackInternalUserToggled({
        collection: collection,
        internal: internal || false
      });

      dispatch(invalidateCollectionUpdateState());
      onClose();
    }
  }, [show, updated]);

  return (
    <Slider.Dialog show={show} width='300' className={styles.confrimationDialog} onClose={onClose}>
      <Slider.Header title={
        internal ? 'turn OFF "Internal use only"?' : 'turn ON "Internal use only"?'
      } />
      <Slider.Body>
        {!internal &&
          <>
            <p className={styles.message}>Turning this ON means no external sharing. Your team can't share this collection outside—secrets stay secret!</p>
          </>
        }

        {internal &&
          <>
            <p className={styles.message}>Turning this feature &apos;off&apos; will mean this collection will no longer be marked for Internal use.</p>
            <p className={styles.message}>Link Generation and Sharing functionalities will be enabled. Your team would be able to share the collection with external users.</p>
          </>
        }
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary"
          icon="paperflite-tick"
          text={updating ? 'Updating...' : 'Confirm'}
          disabled={updating}
          onClick={handleConfirm}
        />
        <Button text="Cancel" disabled={updating} onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface ChooseExperienceDialogProps {
  show: boolean;
  collection: Collection;
  onClose: () => void;
}

export const ChooseExperienceDialog: React.FC<ChooseExperienceDialogProps> = ({ show, collection, onClose }) => {
  const collectionContext = useContext(CollectionContext);

  const [currentStep, setCurrentStep] = React.useState(0);
  const [experience, setExperience] = React.useState(collection.viewerType || DEFAULT_EXPERIENCE);
  const [assetViewer, setAssetViewer] = React.useState(collection.assetViewer || DEFAULT_ASSET_VIEWER);

  const { trackExperienceTypeSelectedEvent, trackViewerTypeSelectedEvent } = useCollectionsAdoption();

  const experiences = useMemo(() => {
    return subscriptionPlanService.getCollectionExperiences().map((experienceId) => {
      return EXPERIENCES.find(item => item.id === experienceId);
    }).filter(item => item);
  }, [subscriptionPlanService.getCollectionExperiences()]);

  const assetViewers = useMemo(() => {
    return subscriptionPlanService.getCollectionAssetViewers().map((viewerId) => {
      return ASSET_VIEWERS.find(item => item.id === viewerId);
    }).filter(item => item);
  }, [subscriptionPlanService.getCollectionAssetViewers()]);

  const nextButtonText = useMemo(() => {
    const finalStep = _.size(assetViewers) == 0 ? 0 : 1;
    if (currentStep === finalStep) {
      return 'Confirm';
    }

    return 'Next';
  }, [currentStep]);

  useEffect(() => {
    if (show) {
      setCurrentStep(0);
      setExperience(collection.viewerType || DEFAULT_EXPERIENCE);
      setAssetViewer(collection.assetViewer || DEFAULT_ASSET_VIEWER);
    }
  }, [show, collection]);

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  }

  const handleExperienceChange = (experience: string) => {
    setExperience(experience);

    trackExperienceTypeSelectedEvent({
      id: collection.id!,
      name: collection.name!,
      experience: experience,
    });
  };

  const handleAssetViewerChange = (viewer: any) => {
    setAssetViewer(viewer);

    trackViewerTypeSelectedEvent({
      id: collection.id!,
      name: collection.name!,
      viewer: viewer,
    });
  };

  const handleConfirm = () => {
    goToNextStep(currentStep);
  }

  const goToNextStep = (step) => {
    const finalStep = _.size(assetViewers) == 0 ? 0 : 1;
    if (step === finalStep) {
      collectionContext.updateCollectionProperty('viewerType', experience);
      collectionContext.updateCollectionProperty('assetViewer', assetViewer);

      onClose();
    } else {
      setCurrentStep(step + 1);
    }
  }

  return (
    <Slider.Dialog show={show} width='300' className={styles.chooseExperienceDialog} onClose={onClose}>
      <Slider.Header title={<>Switch Experience</>} />
      <Slider.Body className={styles.body}>
        <Wizard.Pane className={styles.wizard} currentStep={currentStep} gap={30} onStepChange={handleStepChange}>
          <Wizard.Step name="Choose experience" completed={true}>
            <div className={cx(styles.step, styles.experienceStep)}>
              <ExperienceSelector experiences={experiences}
                selected={experience}
                onSelect={handleExperienceChange}
              />
            </div>
          </Wizard.Step>
          {_.size(assetViewers) > 0 &&
            <Wizard.Step name="Choose Viewer" completed={true}>
              <div className={cx(styles.step, styles.experienceStep)}>
                <AssetViewerSelector viewers={assetViewers}
                  selected={assetViewer}
                  onSelect={handleAssetViewerChange}
                />
              </div>
            </Wizard.Step>
          }
        </Wizard.Pane>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary"
          icon="paperflite-tick"
          text={nextButtonText}
          onClick={handleConfirm}
        />
        <Button text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface GenerateLinkDialogProps {
  show: boolean;
  collection: Collection;
  onClose: () => void;
}
export const GenerateLinkDialog: React.FC<GenerateLinkDialogProps> = ({ show, collection, onClose }) => {
  return (
    <Slider.Dialog
      width={"380px"}
      show={show}
      onClose={onClose}
    >
      {show && (
        <GenerateLink entity={collection} onClose={onClose} />
      )}
    </Slider.Dialog>
  );
}